import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { hasCapability, formatINR } from "@/lib/permissions";
import {
  Plus, Search, Inbox, CheckCircle2, XCircle, PackagePlus, PackageCheck, Clock, AlertTriangle, ClipboardList,
} from "lucide-react";

const STATUS_STYLES = {
  pending: { icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Pending" },
  approved: { icon: CheckCircle2, cls: "bg-sky-50 text-sky-800 border-sky-200", label: "Approved" },
  rejected: { icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Rejected" },
  po_raised: { icon: PackagePlus, cls: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "PO Raised" },
  partially_fulfilled: { icon: PackageCheck, cls: "bg-teal-50 text-teal-800 border-teal-200", label: "Partial" },
  fulfilled: { icon: PackageCheck, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Fulfilled" },
  cancelled: { icon: XCircle, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
};

const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-600",
  normal: "bg-slate-50 text-slate-700",
  high: "bg-amber-100 text-amber-800",
  urgent: "bg-rose-100 text-rose-800",
};

export default function ConsumableRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | to_approve | mine
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");

  const canRaise = hasCapability(user, "raise_consumable_request");
  const canApprove = hasCapability(user, "approve_consumable_request");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "mine") params.mine = true;
      if (tab === "to_approve") params.to_approve = true;
      if (statusFilter) params.status = statusFilter;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/consumable-requests", { params }),
        api.get("/consumable-requests/summary"),
      ]);
      setRows(list || []);
      setSummary(sum);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load requisitions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab, statusFilter]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      `${r.request_number} ${r.requested_by_name || ""} ${r.department || ""} ${
        (r.items || []).map((i) => `${i.code} ${i.name}`).join(" ")
      }`.toLowerCase().includes(query),
    );
  }, [rows, q]);

  const kpis = summary?.counts || {};
  return (
    <div className="space-y-6" data-testid="consumable-requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Stores · Requisitions</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ClipboardList className="w-7 h-7" /> Consumable Requests
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Raise → Approve → Raise PO → Receive → Fulfilled. Stock and ledger update automatically.
          </p>
        </div>
        {canRaise && (
          <Button
            onClick={() => navigate("/consumable-requests/new")}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="raise-request-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Raise Request
          </Button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi icon={<Clock className="w-4 h-4" />} label="Pending" value={kpis.pending || 0} tint="amber" onClick={() => { setTab("all"); setStatusFilter("pending"); }} />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="Approved" value={kpis.approved || 0} tint="sky" onClick={() => { setTab("all"); setStatusFilter("approved"); }} />
        <Kpi icon={<PackagePlus className="w-4 h-4" />} label="PO Raised" value={kpis.po_raised || 0} tint="indigo" onClick={() => { setTab("all"); setStatusFilter("po_raised"); }} />
        <Kpi icon={<PackageCheck className="w-4 h-4" />} label="Partial" value={kpis.partially_fulfilled || 0} tint="teal" onClick={() => { setTab("all"); setStatusFilter("partially_fulfilled"); }} />
        <Kpi icon={<PackageCheck className="w-4 h-4" />} label="Fulfilled" value={kpis.fulfilled || 0} tint="emerald" onClick={() => { setTab("all"); setStatusFilter("fulfilled"); }} />
        <Kpi icon={<XCircle className="w-4 h-4" />} label="Rejected" value={kpis.rejected || 0} tint="rose" onClick={() => { setTab("all"); setStatusFilter("rejected"); }} />
      </div>

      {/* Tabs + Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
            <TabBtn active={tab === "all"} onClick={() => { setTab("all"); }} testid="tab-all">All</TabBtn>
            <TabBtn active={tab === "mine"} onClick={() => { setTab("mine"); }} testid="tab-mine">My requests</TabBtn>
            {canApprove && (
              <TabBtn active={tab === "to_approve"} onClick={() => { setTab("to_approve"); }} testid="tab-to-approve">
                To approve
                {summary?.to_approve > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-red-500 text-white">
                    {summary.to_approve}
                  </span>
                )}
              </TabBtn>
            )}
          </div>
          {tab === "all" && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="status-filter"
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_STYLES).map(([k, s]) => (
                <option key={k} value={k}>{s.label}</option>
              ))}
            </select>
          )}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by request #, item, requester..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="requests-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Request #</th>
                <th className="px-4 py-3 text-left">Items</th>
                <th className="px-4 py-3 text-left">Requester</th>
                <th className="px-4 py-3 text-center">Priority</th>
                <th className="px-4 py-3 text-right">Est. Value</th>
                <th className="px-4 py-3 text-left">Needed by</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                    <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                    No requisitions match the current filter.
                  </td>
                </tr>
              ) : filtered.map((r) => {
                const est = (r.items || []).reduce((s, i) => s + (Number(i.qty_requested) || 0) * (Number(i.unit_cost_at_request) || 0), 0);
                const st = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                const StIcon = st.icon;
                return (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/consumable-requests/${r.id}`)}
                    data-testid={`request-row-${r.request_number}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">{r.request_number}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium">
                        {r.items?.[0]?.name || "—"} {r.items?.length > 1 && <span className="text-slate-400 text-xs">+{r.items.length - 1} more</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{(r.items || []).map((i) => i.code).filter(Boolean).join(", ")}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{r.requested_by_name}</div>
                      <div className="text-[11px] text-slate-500">{r.department || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PRIORITY_STYLES[r.priority] || PRIORITY_STYLES.normal}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{formatINR(est)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.needed_by || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`gap-1 border ${st.cls}`}>
                        <StIcon className="w-3 h-3" /> {st.label}
                      </Badge>
                      {r.po_number && (
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">PO: {r.po_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" && canApprove && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 inline" title="Awaiting your approval" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 border-r border-slate-200 last:border-r-0 font-medium ${
        active ? "bg-[#7C1D3A] text-white" : "bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function Kpi({ icon, label, value, tint = "slate", onClick }) {
  const tints = {
    slate: "bg-slate-50 text-slate-700 hover:bg-slate-100",
    emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    amber: "bg-amber-50 text-amber-700 hover:bg-amber-100",
    rose: "bg-rose-50 text-rose-700 hover:bg-rose-100",
    sky: "bg-sky-50 text-sky-700 hover:bg-sky-100",
    indigo: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    teal: "bg-teal-50 text-teal-700 hover:bg-teal-100",
  };
  return (
    <button onClick={onClick} className={`${tints[tint]} border-0 rounded-lg p-3 text-left transition cursor-pointer`}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-semibold tracking-wider opacity-80">
        {icon} {label}
      </div>
      <div className="mt-1 text-2xl font-bold font-mono">{value}</div>
    </button>
  );
}
